/**
 * Typed IPC channel definitions shared between main and renderer processes.
 *
 * Convention:
 * - Request/response channels use `invoke` (renderer calls, main responds)
 * - Push channels use `on` (main pushes to renderer)
 */
/**
 * One complete, canonical `FeatureFlagsSnapshot` default. Stubs, dev
 * mocks, and test fixtures MUST spread from this and override only the
 * fields the case cares about — never hand-enumerate every field.
 *
 * Why: `FeatureFlagsSnapshot` is an exhaustive type; a hand-enumerated
 * mock breaks the moment the type gains a field, and the break only
 * surfaces in the cross-platform `typecheck:tests` leg (the plain
 * `typecheck` skips test files), three platforms deep in CI. Spreading
 * from this default makes a field addition invisible to every mock.
 * (Crucible #2197; rule: exhaustive-mocks-from-default-and-test-typecheck.)
 *
 * Values mirror the shipped production defaults documented on each
 * field above: opt-OUT flags default ON, opt-IN flags default OFF. Every
 * optional field is set explicitly so the default is genuinely complete.
 */
export const DEFAULT_FEATURE_FLAGS = {
    interviewEngineV1: true,
    agentTransparency: true,
    lazyDashboardLoad: true,
    foundryPrototypes: false,
    cloudVmV1: false,
    newUi: true,
    craftMode: false,
    livePreview: true,
    scaffoldFirst: false,
    inAppPairing: true,
    designTab: true,
    tileImageUpload: true,
};
/**
 * ID prefix for the chat message synthesised from the Create dialog's
 * "Initial AI Prompt" field. Set in `main/chat-history.ts`'s
 * `persistInitialPromptAsChatMessage` and matched in
 * `renderer/pages/Workspace.tsx`'s `chat:load` effect to auto-send the
 * persisted prompt to the agent (issue #145). The shared constant
 * keeps the two call sites in lockstep — historically this was a magic
 * string in both places, and forking would silently break the
 * auto-send while leaving everything else looking healthy.
 */
export const INITIAL_PROMPT_MESSAGE_ID_PREFIX = 'initial-prompt-';
/**
 * Hardcoded allowlist of repos the `/file-bug` SKILL may target. v1
 * keeps this in code (no `CrucibleConfig` field, no Settings UI) per
 * the brief; v1.1 may surface it as a configurable list. Both the
 * SKILL prose and the main-side `bug:file` handler validate against
 * this constant, so a SKILL that drifts (e.g. by misreading
 * frontmatter) still gets refused by main with a structured error.
 */
export const BUG_REPO_ALLOWLIST = [
    'Volley-Inc/crucible',
    'Volley-Inc/hello-weekend',
];
//# sourceMappingURL=ipc-channels.js.map